import { config } from './config';

const API_BASE = 'https://api-iam.intercom.io';

/** Guard against a scheme-less config value (e.g. "host.com") becoming a
 *  relative redirect. */
const withScheme = (url: string): string => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

/* eslint-disable @typescript-eslint/no-explicit-any */
const loadWidget = (appId: string): void => {
    const w = window as any;
    if (w.Intercom) return;
    const i: any = (...args: any[]) => i.c(args);
    i.q = [];
    i.c = (args: any[]) => i.q.push(args);
    w.Intercom = i;
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${appId}`;
    document.head.appendChild(script);
};

/** Boot the Messenger with an Intercom JWT so Intercom establishes its session
 *  cookie. Does not navigate — the user chooses Continue or Log out. */
const boot = (jwt: string, appId: string): void => {
    loadWidget(appId);
    (window as any).Intercom('boot', {
        api_base: API_BASE,
        app_id: appId,
        intercom_user_jwt: jwt,
        hide_default_launcher: true,
    });
};

/** Which Help Center Continue routes to; set by whichever boot path ran. */
let partnerSession = false;

/** Customer flow: exchange a Cognito id token for an Intercom Messenger JWT,
 *  then boot. */
export const mintAndBoot = async (idToken: string): Promise<void> => {
    partnerSession = false;
    const res = await fetch(`${config.apiBase}/tickets/V1/portal/jwt`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`Sign-in failed (${res.status})`);
    const { jwt, appId } = (await res.json()) as { jwt: string; appId: string };
    boot(jwt, appId);
};

/** Partner handoff: boot with a JWT already minted by Insights (passed in the
 *  URL fragment), using the app id baked into config. */
export const bootWithJwt = (jwt: string): void => {
    partnerSession = true;
    boot(jwt, config.intercomAppId);
};

/** Navigate to the audience's Help Center; the booted session cookie forwards there. */
export const goToHelpCenter = (): void => {
    const destination = partnerSession && config.partnerHelpCenterUrl ? config.partnerHelpCenterUrl : config.helpCenterUrl;
    window.location.href = withScheme(destination);
};

/** Domains a cookie could be scoped to, from host-only up to the registrable
 *  parent — so a `.moutfitters.com`-scoped Intercom cookie is also expired. */
const cookieDomains = (): string[] => {
    const parts = window.location.hostname.split('.');
    const domains = ['']; // host-only (no Domain attribute)
    for (let i = 0; i < parts.length - 1; i += 1) {
        domains.push(`.${parts.slice(i).join('.')}`);
    }
    return domains;
};

const expireCookie = (name: string): void => {
    cookieDomains().forEach((domain) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`;
    });
};

/** Fully end the Intercom session and wipe every Intercom cookie + storage entry
 *  (across host and parent domains) so a prior identity can't leak into the next
 *  sign-in or forward to the Help Center. */
export const clearIntercom = (): void => {
    try {
        (window as any).Intercom?.('shutdown');
    } catch {
        /* widget may not be booted */
    }

    document.cookie.split(';').forEach((cookie) => {
        const name = cookie.split('=')[0].trim();
        if (name.toLowerCase().startsWith('intercom')) expireCookie(name);
    });

    [window.localStorage, window.sessionStorage].forEach((store) => {
        try {
            Object.keys(store)
                .filter((key) => key.toLowerCase().includes('intercom'))
                .forEach((key) => store.removeItem(key));
        } catch {
            /* storage may be unavailable */
        }
    });
};
